/** @jsxImportSource @emotion/react */

import React from "react";
import {css} from "@emotion/react";
import {TextButton} from "js/common/views/inputs/buttons";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import * as Groups from "js/common/groups";

const ScheduleAccessDialog = ({
  usersWithoutAccess,
  groupsWithoutAccess,
  usersWithAccess,
  groupsWithAccess,
  externalRecipients,
  onSaveSchedule,
  onCancel
}) => {
  const {theme} = React.useContext(CustomThemeContext);

  return (
    <div css={containerStyle}>
      <div css={contentStyle}>
        <div css={sectionHeaderStyle}>Users and Groups without access</div>
        <p css={sectionDescriptionStyle}>Recipients will receive a PDF report only.</p>

        <div css={listContainerStyle}>
          {groupsWithoutAccess.sort(Groups.compareGroupsByBreadcrumbs).map((group) => {
            const breadcrumbs = Groups.getGroupBreadcrumbs(group.get("id"));
            return (
              <div key={group.get("id")} css={listItemStyle(theme)}>
                <div css={userInfoStyle}>
                  <i className="bhi-users" style={{marginRight: 8, fontSize: 14, opacity: 0.5}} />
                  <span>
                    {breadcrumbs.map((g, index) => (
                      <React.Fragment key={g.id}>
                        {index > 0 && <i className="bhi-next" style={{margin: "0 4px", fontSize: 10}} />}
                        {g.name}
                      </React.Fragment>
                    ))}
                  </span>
                </div>

                <span css={noAccessStyle}>No Access</span>
              </div>
            );
          })}

          {usersWithoutAccess
            .sortBy((item) => item.get("name").toLowerCase())
            .map((item) => {
              return (
                <div key={item.get("id")} css={listItemStyle(theme)}>
                  <div css={userInfoStyle}>
                    <i className="bhi-person" style={{marginRight: 8, fontSize: 14, opacity: 0.5}} />
                    <span>{item.get("name")}</span>
                  </div>

                  <span css={noAccessStyle}>No Access</span>
                </div>
              );
            })}

          {externalRecipients
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .map((item) => {
              return (
                <div key={item} css={listItemStyle(theme)}>
                  <div css={userInfoStyle}>
                    <span style={{position: "relative"}}>
                      <i className="bhi-company" style={{marginRight: 8, marginLeft: 3, fontSize: 14, opacity: 0.5}} />
                      <span css={strikethroughStyle(theme)}></span>
                    </span>
                    <span>{item}</span>
                  </div>

                  <span css={badgeStyle(theme)}>External Recipient</span>
                  <span css={noAccessStyle}>No Access</span>
                </div>
              );
            })}
        </div>

        {(usersWithAccess.size > 0 || groupsWithAccess.size > 0) && (
          <div style={{marginTop: 32, marginBottom: 32}}>
            <div css={sectionHeaderStyle}>Users and Groups with access</div>
            <p css={sectionDescriptionStyle}>Recipients will receive a PDF report and a link to the report.</p>
            <div css={listContainerStyle}>
              {groupsWithAccess.sort(Groups.compareGroupsByBreadcrumbs).map((group) => {
                const breadcrumbs = Groups.getGroupBreadcrumbs(group.get("id"));
                return (
                  <div key={group.get("id")} css={listItemStyle(theme)}>
                    <div css={userInfoStyle}>
                      <i className="bhi-users" style={{marginRight: 8, fontSize: 14, opacity: 0.5}} />
                      <span>
                        {breadcrumbs.map((g, index) => (
                          <React.Fragment key={g.id}>
                            {index > 0 && <i className="bhi-next" style={{margin: "0 4px", fontSize: 10}} />}
                            {g.name}
                          </React.Fragment>
                        ))}
                      </span>
                    </div>

                    <span css={accessGrantedStyle}>View Access</span>
                  </div>
                );
              })}
              {usersWithAccess
                .sortBy((item) => item.get("name").toLowerCase())
                .map((item) => {
                  return (
                    <div key={item.get("id")} css={listItemStyle(theme)}>
                      <div css={userInfoStyle}>
                        <i className="bhi-person" style={{marginRight: 8, fontSize: 14, opacity: 0.5}} />
                        <span>{item.get("name")}</span>
                      </div>

                      <span css={accessGrantedStyle}>View Access</span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      <div css={footerStyle(theme)}>
        <TextButton label="CANCEL" onClick={onCancel} style={{marginRight: 12}} />
        <TextButton type="primary" label="SAVE SCHEDULE" onClick={onSaveSchedule} />
      </div>
    </div>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
  margin-top: 14px;
`;

const contentStyle = css`
  flex: 1;
  overflow-y: auto;
`;

const sectionHeaderStyle = css`
  font-size: 16px;
  margin-bottom: 0px;
`;

const sectionDescriptionStyle = css`
  font-size: 11px;
  margin-bottom: 16px;
`;

const listContainerStyle = css`
  padding: 0 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const badgeStyle = (theme) => css`
  background-color: ${theme.palette.border.lightest};
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 12px;
`;

const listItemStyle = (theme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid ${theme.palette.border.lightest};
`;

const userInfoStyle = css`
  display: flex;
  align-items: center;
  font-size: 13px;
  flex-grow: 1;
`;

const noAccessStyle = css`
  font-size: 11px;
  text-transform: uppercase;
`;

const accessGrantedStyle = css`
  font-size: 11px;
  text-transform: uppercase;
`;

const strikethroughStyle = (theme) => css`
  width: 2px;
  height: 20px;
  rotate: 45deg;
  position: absolute;
  top: 0;
  left: 8px;
  background-color: ${theme.palette.text.lighter};
`;

const footerStyle = (theme) => css`
  display: flex;
  justify-content: flex-end;
  padding: 16px 15px 8px;
  margin: 5px -15px 0;
  border-top: 1px solid ${theme.palette.border.light};
`;

export default ScheduleAccessDialog;
